import { all, call, put, takeEvery, select } from 'redux-saga/effects';
import {
  defineKqlQuery,
  searchSubmissions,
  fetchBridgedResource,
  fetchCategory,
} from '@kineticdata/react';
import {
  selectServicesKapp,
  selectSurveyKapp,
  C,
} from '@kineticdata/bundle-common';
import { actions, types } from '../modules/home';
import { OrderedMap } from 'immutable';

export function* fetchHomeDataRequestSaga({ payload: countsOnly }) {
  yield all(
    [
      !countsOnly && put(actions.fetchNewsRequest()),
      put(actions.fetchHomeRequestsRequest(countsOnly)),
      put(actions.fetchHomeApprovalsRequest(countsOnly)),
      put(actions.fetchHomeActionsRequest(countsOnly)),
      put(actions.fetchHomeSurveysCount()),
      !countsOnly && put(actions.fetchPopularFormsRequest()),
      !countsOnly && put(actions.fetchRecentFormsRequest()),
      !countsOnly && put(actions.fetchDataCenterStatusRequest()),
      // Commented out because the current source of this data no longer works.
      // This will need to be updated to fetch data from a new source, likely a datastore.
      // put(actions.fetchMaintenanceScheduleRequest()),
    ].filter(Boolean),
  );
}

export function* fetchNewsRequestSaga() {
  const searcher = defineKqlQuery()
    .in('coreState', 'coreState')
    .equals(`values[${C.FIELD_STATUS}]`, 'status')
    .greaterThanOrEquals(`values[${C.FIELD_EXPIRATION_DATE}]`, 'date');

  const { submissions, error, count } = yield call(searchSubmissions, {
    kapp: C.KAPP_SLUG_ADMIN,
    form: C.FORM_SLUG_NEWS,
    limit: 1000,
    search: {
      q: searcher.end()({
        coreState: [C.CORE_STATE_DRAFT, C.CORE_STATE_SUBMITTED],
        status: 'Active',
        date: new Date().toISOString().substring(0, 10),
      }),
      orderBy: `values[${C.FIELD_EXPIRATION_DATE}],createdAt`,
      direction: 'DESC',
      include: ['details', 'values', 'form.attributes[Image]'],
    },
    count: true,
  });

  if (error) {
    yield put(actions.fetchNewsFailure(error));
  } else {
    yield put(
      actions.fetchNewsSuccess({
        data: submissions
          // Sort client side since we're querying by Expiration Date
          // but want to sort by createdAt.
          // Showcase records first, then others, in descending createdAt order.
          .sort(
            (a, b) =>
              a.values['Showcase'][0] !== b.values['Showcase'][0]
                ? a.values['Showcase'][0] === 'Showcase'
                  ? -1
                  : 1
                : a.createdAt > b.createdAt
                  ? -1
                  : b.createdAt > a.createdAt
                    ? 1
                    : 0,
          )
          // Show only 5 on the home page.
          .slice(0, 5),
        count,
      }),
    );
  }
}

export function* fetchHomeRequestsRequestSaga({ payload: countsOnly }) {
  const kapp = yield select(selectServicesKapp);
  const profile = yield select(state => state.app.profile);
  const searcher = defineKqlQuery()
    .equals('type', 'type')
    .equals('coreState', 'coreState')
    .or()
    .equals('submittedBy', 'username')
    .equals(`values[${C.FIELD_REQUESTED_FOR}]`, 'username')
    .end();

  const { submissions, error, count } = yield call(searchSubmissions, {
    kapp: (kapp && kapp.slug) || 'services',
    limit: countsOnly ? 0 : 5,
    search: {
      q: searcher.end()({
        type: C.REQUEST_FORM_TYPE,
        coreState: C.CORE_STATE_SUBMITTED,
        username: profile.username,
      }),
      include: ['details', 'values', 'form', 'form.attributesMap[Icon]'],
    },
    count: true,
  });

  if (error) {
    yield put(actions.fetchHomeRequestsFailure(error));
  } else {
    yield put(
      actions.fetchHomeRequestsSuccess({
        data: countsOnly ? undefined : submissions,
        count,
      }),
    );
  }
}

export function* fetchHomeApprovalsRequestSaga({ payload: countsOnly }) {
  const kapp = yield select(selectServicesKapp);
  const profile = yield select(state => state.app.profile);
  const teams = profile.memberships.map(m => m.team.name);
  const searcher = defineKqlQuery()
    .equals('type', 'type')
    .equals('coreState', 'coreState');
  if (teams.length > 0) {
    searcher
      .or()
      .equals(`values[${C.FIELD_ASSIGNED_INDIVIDUALS}]`, 'username')
      .in(`values[${C.FIELD_ASSIGNED_TEAMS}]`, 'teams')
      .end();
  } else {
    searcher.equals(`values[${C.FIELD_ASSIGNED_INDIVIDUALS}]`, 'username');
  }

  const { submissions, error, count } = yield call(searchSubmissions, {
    kapp: (kapp && kapp.slug) || 'services',
    limit: countsOnly ? 0 : 3,
    search: {
      q: searcher.end()({
        type: C.APPROVAL_FORM_TYPE,
        coreState: C.CORE_STATE_DRAFT,
        username: profile.username,
        teams,
      }),
      include: ['details', 'values', 'form', 'form.attributesMap[Icon]'],
    },
    count: true,
  });

  if (error) {
    yield put(actions.fetchHomeApprovalsFailure(error));
  } else {
    yield put(
      actions.fetchHomeApprovalsSuccess({
        data: countsOnly ? undefined : submissions,
        count,
      }),
    );
  }
}

export function* fetchHomeActionsRequestSaga({ payload: countsOnly }) {
  const kapp = yield select(selectServicesKapp);
  const profile = yield select(state => state.app.profile);
  const teams = profile.memberships.map(m => m.team.name);
  const searcher = defineKqlQuery()
    .equals('type', 'type')
    .equals('coreState', 'coreState');
  if (teams.length > 0) {
    searcher
      .or()
      .equals(`values[${C.FIELD_ASSIGNED_INDIVIDUALS}]`, 'username')
      .in(`values[${C.FIELD_ASSIGNED_TEAMS}]`, 'teams')
      .end();
  } else {
    searcher.equals(`values[${C.FIELD_ASSIGNED_INDIVIDUALS}]`, 'username');
  }

  const { submissions, error, count } = yield call(searchSubmissions, {
    kapp: (kapp && kapp.slug) || 'services',
    limit: countsOnly ? 0 : 3,
    search: {
      q: searcher.end()({
        type: C.ACTION_FORM_TYPE,
        coreState: C.CORE_STATE_DRAFT,
        username: profile.username,
        teams,
      }),
      include: ['details', 'values', 'form', 'form.attributesMap[Icon]'],
    },
    count: true,
  });

  if (error) {
    yield put(actions.fetchHomeActionsFailure(error));
  } else {
    yield put(
      actions.fetchHomeActionsSuccess({
        data: countsOnly ? undefined : submissions,
        count,
      }),
    );
  }
}

export function* fetchHomeSurveysCountSaga() {
  const kapp = yield select(selectSurveyKapp);
  const profile = yield select(state => state.app.profile);
  const searcher = defineKqlQuery()
    .equals('type', 'type')
    .equals('coreState', 'coreState')
    .in(`values[${C.FIELD_ASSIGNED_INDIVIDUAL}]`, 'user');

  const { error, count } = yield call(searchSubmissions, {
    kapp: (kapp && kapp.slug) || 'survey',
    limit: 0,
    search: {
      q: searcher.end()({
        type: C.SURVEY_FORM_TYPE,
        coreState: C.CORE_STATE_DRAFT,
        user: [profile.email, profile.username],
      }),
      include: ['details', 'values', 'form', 'form.attributesMap[Icon]'],
    },
    count: true,
  });

  yield put(actions.fetchHomeSurveysCountComplete({ error, count }));
}

export function* fetchPopularFormsRequestSaga() {
  const kapp = yield select(selectServicesKapp);

  const { category, error } = yield call(fetchCategory, {
    kappSlug: (kapp && kapp.slug) || 'services',
    categorySlug: C.POPULAR_FORMS_CATEGORY_SLUG,
    include: [
      'categorizations.form',
      'categorizations.form.attributesMap[Icon]',
      'categorizations.form.attributesMap[Image]',
      'categorizations.form.kapp.attributesMap[Default Form Image]',
    ].join(','),
  });

  if (error) {
    yield put(actions.fetchPopularFormsFailure(error));
  } else {
    yield put(
      actions.fetchPopularFormsSuccess({
        data: category.categorizations
          .map(cat => cat.form)
          .filter(form => C.REQUEST_FORM_STATUSES.includes(form.status))
          .slice(0, 12),
      }),
    );
  }
}

export function* fetchRecentFormsRequestSaga() {
  const kapp = yield select(selectServicesKapp);
  const profile = yield select(state => state.app.profile);
  const searcher = defineKqlQuery()
    .equals('type', 'type')
    .equals('coreState', 'coreState')
    .equals('submittedBy', 'username');

  const { submissions, error } = yield call(searchSubmissions, {
    kapp: (kapp && kapp.slug) || 'services',
    limit: 50,
    search: {
      q: searcher.end()({
        type: C.REQUEST_FORM_TYPE,
        coreState: C.CORE_STATE_SUBMITTED,
        username: profile.username,
      }),
      include: [
        'form',
        'form.attributes[Icon]',
        'form.attributes[Image]',
        'form.kapp.attributes[Default Form Image]',
      ],
    },
  });

  if (error) {
    yield put(actions.fetchRecentFormsFailure(error));
  } else {
    yield put(
      actions.fetchRecentFormsSuccess({
        data: submissions
          .reduce(
            (forms, submission) =>
              forms.set(submission.form.slug, submission.form),
            OrderedMap(),
          )
          .toList()
          .take(12)
          .toJS(),
      }),
    );
  }
}

export function* fetchDataCenterStatusRequestSaga() {
  const searcher = defineKqlQuery().equals(
    `values[${C.FIELD_STATUS}]`,
    'status',
  );

  const { submissions, error } = yield call(searchSubmissions, {
    kapp: C.KAPP_SLUG_ADMIN,
    form: C.FORM_SLUG_DATA_CENTER_STATUS,
    limit: 100,
    search: {
      q: searcher.end()({
        status: 'active',
      }),
      orderBy: 'updatedAt',
      direction: 'DESC',
      include: ['details', 'values'],
    },
  });

  if (error) {
    yield put(actions.fetchDataCenterStatusFailure(error));
  } else {
    yield put(
      actions.fetchDataCenterStatusSuccess({
        data: submissions
          // Sort client side since we want Outages to show up first.
          .sort(
            (a, b) =>
              (a.values['Impact'] === 'Major Outage' ? 0 : 1) -
              (b.values['Impact'] === 'Major Outage' ? 0 : 1),
          ),
      }),
    );
  }
}

export function* fetchMaintenanceScheduleRequestSaga() {
  const kapp = yield select(selectServicesKapp);
  const { records, error } = yield call(fetchBridgedResource, {
    kappSlug: kapp && kapp.slug,
    formSlug: C.FORM_SLUG_SHARED_RESOURCES,
    bridgedResourceName: 'Maintenance Schedule',
  });

  if (error) {
    yield put(actions.fetchMaintenanceScheduleFailure(error));
  } else {
    yield put(
      actions.fetchMaintenanceScheduleSuccess({
        data: records,
      }),
    );
  }
}

export function* watchHome() {
  yield takeEvery(types.FETCH_HOME_DATA_REQUEST, fetchHomeDataRequestSaga);
  yield takeEvery(types.FETCH_NEWS_REQUEST, fetchNewsRequestSaga);
  yield takeEvery(
    types.FETCH_HOME_REQUESTS_REQUEST,
    fetchHomeRequestsRequestSaga,
  );
  yield takeEvery(
    types.FETCH_HOME_APPROVALS_REQUEST,
    fetchHomeApprovalsRequestSaga,
  );
  yield takeEvery(
    types.FETCH_HOME_ACTIONS_REQUEST,
    fetchHomeActionsRequestSaga,
  );
  yield takeEvery(types.FETCH_HOME_SURVEYS_COUNT, fetchHomeSurveysCountSaga);
  yield takeEvery(
    types.FETCH_POPULAR_FORMS_REQUEST,
    fetchPopularFormsRequestSaga,
  );
  yield takeEvery(
    types.FETCH_RECENT_FORMS_REQUEST,
    fetchRecentFormsRequestSaga,
  );
  yield takeEvery(
    types.FETCH_DATA_CENTER_STATUS_REQUEST,
    fetchDataCenterStatusRequestSaga,
  );
  yield takeEvery(
    types.FETCH_MAINTENANCE_SCHEDULE_REQUEST,
    fetchMaintenanceScheduleRequestSaga,
  );
}
